import { Responsive } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackMenuActionClick, trackMenuClick } from './tracking';
import { setChatbotMenu } from '../../actions/chatbot/actions';
import { openChatbotFiltersModal } from '../../actions/chatbotFiltersModal';
import { ChatbotDesktopMenu, ChatbotMobileMenu } from '../../components/ChatbotMenu';
import { chatbotSelector } from '../../selectors';
import { getChatbotActionButtons } from '../../thunks/chatbot/getChatbotActionButtons';
import { sendChatbotMessage } from '../../thunks/chatbot/sendChatbotMessage';
import { IChatbotActionButtonSchema } from '../../types/chatbot';

export const ChatbotMenuContainer = () => {
  const { menuActionButtons, messageSending: isMessageSending, chatbotMenu, menuText } = useSelector(chatbotSelector);
  const { menuIsOpen: isMenuOpen, menuOpenSource } = chatbotMenu;

  const dispatch = useDispatch();

  const toggleMenu = React.useCallback(() => {
    /* istanbul ignore else */
    if (!isMenuOpen) {
      trackMenuClick();
    }

    dispatch(setChatbotMenu({ menuIsOpen: !isMenuOpen, menuOpenSource: 'menu' }));
  }, [dispatch, isMenuOpen]);

  const handleItemClick = React.useCallback(
    /* istanbul ignore next */
    (menuActionButtons: IChatbotActionButtonSchema) => {
      if (isMessageSending) {
        return;
      }

      const { analyticsType, payload, actionType, link, inputText, text } = menuActionButtons;

      dispatch(setChatbotMenu({ menuIsOpen: false, menuOpenSource }));

      trackMenuActionClick(analyticsType, menuOpenSource);

      if (actionType === 'payload') {
        dispatch(getChatbotActionButtons(payload, text));

        return;
      }

      /* istanbul ignore next */
      if (actionType === 'sendMessage') {
        dispatch(sendChatbotMessage(inputText || ''));

        return;
      }

      /* istanbul ignore else */
      if (actionType === 'deeplink' && link?.includes('chat-bot-profiling')) {
        dispatch(openChatbotFiltersModal());

        return;
      }

      if (link) {
        window.open(link, '_blank');
      }
    },
    [dispatch, isMessageSending, menuOpenSource],
  );

  return (
    <>
      <Responsive desktop>
        <ChatbotDesktopMenu
          isOpen={isMenuOpen}
          toggleMenu={toggleMenu}
          actionButtons={menuActionButtons}
          onItemClick={handleItemClick}
          disabled={isMessageSending}
          menuText={menuText}
        />
      </Responsive>
      <Responsive tablet phone>
        <ChatbotMobileMenu
          isOpen={isMenuOpen}
          toggleMenu={toggleMenu}
          actionButtons={menuActionButtons}
          onItemClick={handleItemClick}
          disabled={isMessageSending}
          menuText={menuText}
        />
      </Responsive>
    </>
  );
};
